import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  type LeadUpdateRequest,
  leadUpdateRequestSchema,
} from "@pbz/schema/lead";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Loader,
} from "@pbz/ui-kit";

import { FormLabel } from "@/components/FormLabel";
import { useUpdateLeadDetails } from "@/queries/lead";
import {
  useGetBankingInsurers,
  useGetEmploymentTypes,
  useGetProfessionTypes,
} from "@/queries/master";
import { CREDIT_HISTORY_OPTIONS, DD_TYPES } from "@/utils/constants";

interface AdditionalDetailsProps {
  onSubmit?: () => void;
  defaultValues?: Partial<LeadUpdateRequest>;
  leadId: number;
}

const AdditionalDetails: React.FC<AdditionalDetailsProps> = ({
  onSubmit,
  defaultValues,
  leadId,
}) => {
  const form = useForm<LeadUpdateRequest>({
    resolver: zodResolver(leadUpdateRequestSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      leadId,
      ...(defaultValues || {}),
    },
  });

  // Get Employment Types
  const { isLoading: isLoadingEmploymentTypes, data: employmentTypesData } =
    useGetEmploymentTypes();
  const employmentTypes = employmentTypesData?.data || [];

  // Get Profession Types (Designation)
  const { isLoading: isLoadingProfessionTypes, data: professionTypesData } =
    useGetProfessionTypes();
  const professionTypes = professionTypesData?.data || [];

  // Get Banking Insurers
  const { isLoading: isLoadingBankingInsurers, data: bankingInsurersData } =
    useGetBankingInsurers();
  const bankingInsurers = bankingInsurersData?.data || [];
  // Credit History Options
  const creditHistoryOptions = [...CREDIT_HISTORY_OPTIONS];

  // Update Lead Details
  const updateLeadDetailsMutation = useUpdateLeadDetails({
    onSuccess: () => {
      toast.success("Additional details updated successfully");
      if (onSubmit) {
        onSubmit();
      }
    },
    onError: (error) => {
      toast.error(
        error.message ||
          "Failed to update additional details. Please try again.",
      );
    },
  });

  const handleSubmit = (data: LeadUpdateRequest) => {
    updateLeadDetailsMutation.mutate(data);
  };

  const getSelectedEmploymentType = (fieldValue?: {
    dataType: number;
    value: string | number;
  }) => {
    if (!fieldValue?.value) {
      return <span className="text-gray-500">Please Select</span>;
    }
    const selected = employmentTypes.find(
      (type) => type.id === fieldValue.value,
    );
    return selected ? (
      selected.name
    ) : (
      <span className="text-gray-500">Please Select</span>
    );
  };

  const getSelectedProfessionType = (fieldValue?: {
    dataType: number;
    value: string | number;
  }) => {
    if (!fieldValue?.value) {
      return <span className="text-gray-500">Please Select</span>;
    }
    const selected = professionTypes.find(
      (type) => type.id === Number(fieldValue.value),
    );
    return selected ? (
      selected.displayName
    ) : (
      <span className="text-gray-500">Please Select</span>
    );
  };

  const getSelectedBankingInsurer = (fieldValue?: {
    dataType: number;
    value: string | number;
  }) => {
    if (!fieldValue?.value) {
      return <span className="text-gray-500">Please Select</span>;
    }
    const selected = bankingInsurers.find(
      (bank) => bank.id === fieldValue.value,
    );
    return selected ? (
      selected.name
    ) : (
      <span className="text-gray-500">Please Select</span>
    );
  };

  const getSelectedSelectedHistoryOption = (fieldValue?: {
    dataType: number;
    value: string | number;
  }) => {
    if (!fieldValue?.value) {
      return <span className="text-gray-500">Please Select</span>;
    }
    const selected = creditHistoryOptions.find(
      (option) => option.id === fieldValue.value,
    );
    return selected ? (
      selected.name
    ) : (
      <span className="text-gray-500">Please Select</span>
    );
  };

  return (
    <Card className="mb-10 bg-white">
      <CardHeader>
        <CardTitle>Additional Customer Details</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Row 1 */}
              <FormField
                control={form.control}
                name="employerNameDD"
                render={({ field }) => {
                  // Get the nested error for the value property
                  const nestedError =
                    form.formState.errors.employerNameDD?.value;

                  return (
                    <FormItem>
                      <FormLabel>Employer Name</FormLabel>
                      <FormControl>
                        <Input
                          onChange={(e) =>
                            field.onChange({
                              dataType: DD_TYPES.TEXT,
                              value: e.target.value,
                            })
                          }
                          placeholder="Enter Employer Name"
                          value={field.value?.value || ""}
                        />
                      </FormControl>
                      <FormMessage>{nestedError?.message}</FormMessage>
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="employmentTypeDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employment Type</FormLabel>
                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="w-full justify-between"
                            disabled={isLoadingEmploymentTypes}
                            variant="outline"
                          >
                            {getSelectedEmploymentType(field.value)}
                            <div className="flex items-center gap-2">
                              {isLoadingEmploymentTypes && (
                                <Loader className="h-4 w-4" />
                              )}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          {employmentTypes.map((type) => (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              key={type.id}
                              onClick={() =>
                                field.onChange({
                                  dataType: DD_TYPES.DROPDOWN,
                                  value: type.id,
                                })
                              }
                            >
                              {type.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="creditHistoryAgeDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit History Age</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(e) =>
                          field.onChange({
                            dataType: DD_TYPES.TEXT,
                            value: e.target.value,
                          })
                        }
                        placeholder="Enter Credit History Age"
                        value={field.value?.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Row 2 */}
              <FormField
                control={form.control}
                name="creditScoreRangeDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit Score</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(e) =>
                          field.onChange({
                            dataType: DD_TYPES.TEXT,
                            value: e.target.value,
                          })
                        }
                        placeholder="Enter Credit Score"
                        value={field.value?.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="creditCardDesignationDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Designation</FormLabel>
                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="w-full justify-between"
                            disabled={isLoadingProfessionTypes}
                            variant="outline"
                          >
                            {getSelectedProfessionType(field.value)}
                            <div className="flex items-center gap-2">
                              {isLoadingProfessionTypes && (
                                <Loader className="h-4 w-4" />
                              )}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          {professionTypes.map((type) => (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              key={type.id}
                              onClick={() =>
                                field.onChange({
                                  dataType: DD_TYPES.DROPDOWN,
                                  value: type.id,
                                })
                              }
                            >
                              {type.displayName}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="websiteDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employer Website</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(e) =>
                          field.onChange({
                            dataType: DD_TYPES.TEXT,
                            value: e.target.value,
                          })
                        }
                        placeholder="Enter Employer Website"
                        value={field.value?.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Row 3 */}
              <FormField
                control={form.control}
                name="landLineDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employer Landline Number</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(e) =>
                          field.onChange({
                            dataType: DD_TYPES.TEXT,
                            value: e.target.value,
                          })
                        }
                        placeholder="Enter Employer Landline Number"
                        value={field.value?.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="existingCreditDurationDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      How long have you had your current credit cards?
                    </FormLabel>
                    <FormControl>
                      <Input
                        onChange={(e) =>
                          field.onChange({
                            dataType: DD_TYPES.TEXT,
                            value: e.target.value,
                          })
                        }
                        placeholder="Enter in Months"
                        value={field.value?.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="salaryAccountBankDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Salary account Bank</FormLabel>
                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="w-full justify-between"
                            disabled={isLoadingBankingInsurers}
                            variant="outline"
                          >
                            {getSelectedBankingInsurer(field.value)}
                            <div className="flex items-center gap-2">
                              {isLoadingBankingInsurers && (
                                <Loader className="h-4 w-4" />
                              )}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          {bankingInsurers.map((bank) => (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              key={bank.id}
                              onClick={() =>
                                field.onChange({
                                  dataType: DD_TYPES.DROPDOWN,
                                  value: bank.id,
                                })
                              }
                            >
                              {bank.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Row 4 */}
              <FormField
                control={form.control}
                name="sellerIdDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Seller ID (bank side)</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(e) =>
                          field.onChange({
                            dataType: DD_TYPES.TEXT,
                            value: e.target.value,
                          })
                        }
                        placeholder="Enter Seller ID"
                        value={field.value?.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emiratesId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emirates ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Emirates ID"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="creditHistoryDD"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit History Option</FormLabel>
                    <FormControl>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="w-full justify-between"
                            variant="outline"
                          >
                            {getSelectedSelectedHistoryOption(field.value)}
                            <div className="flex items-center gap-2">
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          {creditHistoryOptions.map((option) => (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              key={option.id}
                              onClick={() =>
                                field.onChange({
                                  dataType: DD_TYPES.DROPDOWN,
                                  value: option.id,
                                })
                              }
                            >
                              {option.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end">
              <Button
                className="bg-blue-500 text-white hover:bg-blue-600"
                disabled={updateLeadDetailsMutation.isPending}
                type="submit"
              >
                {updateLeadDetailsMutation.isPending ? "Updating..." : "Update"}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default AdditionalDetails;
